import axios, { CancelToken } from 'axios';
import Request from 'axios-request-handler';
import Auth from 'modules/Auth';
import Local from 'modules/Local';
import store from 'store';
import queryString from 'query-string';
import { API_RESPONSE_ERROR } from 'consts/apiResponseError';
import { HTTP_METHODS, POLLING_INTERVAL } from 'modules/api/constants';
import { SESSION_RECORDER_ENABLED } from 'components/common/SessionRecorderButton/consts';
import { addNotifications, addToastNotification } from 'store/notification/reducer';
import { axiosNetworkDebuggerInterceptor } from './axiosNetworkDebuggerInterceptor';
import { apiErrorHandler, errorHandler } from '../ErrorHandle';

const attachFreshToken = async (config) => {
  if (config.headers && !config.skipAuth) {
    const token = await Auth.getToken();
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
};

// Axios rejects from inside the library, after the application call has left the stack.
// Capture the caller while createRequest is still running and append those frames on failure.
const captureCallerStack = () => {
  const { stack } = new Error();
  if (!stack) return undefined;
  return stack.split('\n').slice(2).join('\n');
};

const appendCallerStack = (error) => {
  const callerStack = error?.config?.datadogCallerStack;
  if (callerStack && typeof error.stack === 'string' && !error.datadogCallerStackAppended) {
    error.stack = `${error.stack}\n${callerStack}`;
    error.datadogCallerStackAppended = true;
  }
  return Promise.reject(error);
};

let interceptorsRegistered = false;

export const setupRequestInterceptors = () => {
  if (interceptorsRegistered) return;
  interceptorsRegistered = true;
  axios.interceptors.request.use(attachFreshToken);
  axios.interceptors.response.use((response) => response, appendCallerStack);
  axiosNetworkDebuggerInterceptor.interceptors.request.use(attachFreshToken);
  axiosNetworkDebuggerInterceptor.interceptors.response.use((response) => response, appendCallerStack);
};

let pendingRequests = 0;

const showLoader = (isShowLoader) => {
  pendingRequests += 1;
  if (isShowLoader) {
    const loaderElement = document.getElementById('loader-container');

    if (loaderElement) {
      loaderElement.style.display = 'block';
    }
  }
};

const hideLoader = () => {
  pendingRequests -= 1;
  if (pendingRequests <= 0) {
    pendingRequests = 0;

    const loaderElement = document.getElementById('loader-container');

    if (loaderElement) {
      loaderElement.style.display = 'none';
    }
  }
};

export const getRequestOptions = ({
  headers: { 'env-id': envId, 'reseller-env-id': resellerEnvId, 'publisher-id': publisherId, ...restHeaders } = {},
  ...restOptions
} = {}) => {
  if (restOptions.ignoreHeaders) {
    return {
      ...restOptions,
      headers: undefined,
      skipAuth: true,
    };
  }

  const { token } = Auth.getData();

  return {
    ...restOptions,
    headers: {
      Authorization: `Bearer ${token}`,
      'env-id': restOptions.envId || Local.getEnvironmentId(),
      ...(restOptions.resellerEnvId && { 'reseller-env-id': restOptions.resellerEnvId }),
      ...restHeaders,
      ...(publisherId === null || {}),
    },
  };
};

const handleSuccess = (response, options) => {
  hideLoader();

  const apiErrors = apiErrorHandler(response);
  if (apiErrors.length) {
    const { dispatch } = store;
    dispatch(addToastNotification({ description: apiErrors, timeout: options.notificationTimeout, type: 'Failure' }));
    const e = new Error(JSON.stringify(response.data));
    e.name = 'customError';
    return Promise.reject(e);
  }

  return response;
};

const getFormattedViolations = (violations, options) => {
  const errors = [];

  violations.campaignErrors?.forEach((description) => {
    errors.push({
      header: 'Something went wrong',
      description,
      timeout: options.notificationTimeout,
      type: 'Failure',
    });
  });

  if (violations.dealLineErrors) {
    errors.push(
      ...Object.values(violations.dealLineErrors)
        .map(({ fieldErrors, dealLineName }) => {
          return fieldErrors.map(({ message }) => ({
            header: dealLineName,
            description: message,
            timeout: options.notificationTimeout,
            type: 'Failure',
          }));
        })
        .flat(),
    );
  }

  return errors;
};

export const isCanceledError = (error) => {
  return axios.isCancel(error) || (error.name === 'CanceledError' && error.code === 'ERR_CANCELED');
};

// eslint-disable-next-line sonarjs/cognitive-complexity
const handleError = (error, options) => {
  hideLoader();

  if (isCanceledError(error)) return Promise.reject(error);

  if (error.response?.status === 401) {
    return Promise.reject(error);
  }

  if (options?.suppressNetworkErrorHandle && error.message === API_RESPONSE_ERROR.NETWORK_ERROR) {
    return Promise.reject(error);
  }

  if (options?.skipNotificationFor?.(error)) {
    return Promise.reject(error);
  }

  if (error.name !== 'customError' && !options.ignoreNotifyError) {
    // check not backend validation error, it is already handled in success block
    let errorMsgs = apiErrorHandler(error.response);
    if (!errorMsgs.length) {
      errorMsgs = errorHandler(error);
    }

    if (errorMsgs.length) {
      const { dispatch } = store;
      const violations = error.response?.data?.violations;

      if (violations && (violations.campaignErrors?.length || Object.keys(violations.dealLineErrors || {}).length)) {
        dispatch(addNotifications(getFormattedViolations(violations, options)));
      } else {
        dispatch(addToastNotification({ error, timeout: options.notificationTimeout, type: 'Failure' }));
      }
    }
  }

  return Promise.reject(error);
};

const createRequest = (options) => {
  const { method = HTTP_METHODS.GET, cancelable = true } = options;
  const { url, query } = queryString.parseUrl(options.url);

  let cancel;
  const poll = (callback, interval = POLLING_INTERVAL) => {
    showLoader(options.showLoader !== false);

    query.updateStamp = 0;
    const request = new Request(queryString.stringifyUrl({ url, query }), {
      ...getRequestOptions(options),
      datadogCallerStack: captureCallerStack(),
    });
    cancel = request.cancel;
    return request
      .poll(interval)
      [method.toLowerCase()]((response) => {
        query.updateStamp = response.data.updateStamp;
        request.setUrl(queryString.stringifyUrl({ url, query }));
        return callback(response);
      })
      .then((response) => handleSuccess(response, options))
      .catch((error) => handleError(error, options));
  };

  const send = () => {
    showLoader(options.showLoader);

    const requestFn = SESSION_RECORDER_ENABLED ? axiosNetworkDebuggerInterceptor : axios;

    const requestConfig = {
      ...getRequestOptions(options),
      datadogCallerStack: captureCallerStack(),
      url: queryString.stringifyUrl({ url, query }),
      cancelToken: new CancelToken((c) => {
        cancel = c;
      }),
    };

    return requestFn(requestConfig)
      .then((response) => handleSuccess(response, options))
      .catch((error) => handleError(error, options));
  };

  return {
    send,
    poll,
    cancel: () => {
      if (cancelable && cancel) {
        cancel();
      }
    },
  };
};

export default createRequest;
